import React, { createContext, useContext, useState, useEffect } from "react";
import apiClient, { projectsApi, promptsApi, systemPromptsApi, settingsApi, footnotesApi } from "../api/client";
import { encryptionService } from "../utils/encryption";
import { dataService } from "../utils/storage";
import { useAuth } from "./AuthContext";

const ProjectContext = createContext();

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error("useProject must be used within a ProjectProvider");
  }
  return context;
};

export const ProjectProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [projects, setProjects] = useState([]);
  const [prompts, setPrompts] = useState({});
  const [systemPrompts, setSystemPrompts] = useState([]);
  const [settings, setSettings] = useState({});
  const [footnotes, setFootnotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [encryptionSupported, setEncryptionSupported] = useState(true);

  // Reset data when user changes
  useEffect(() => {
    if (!isAuthenticated || !user) {
      // Clear all data when user logs out
      setProjects([]);
      setPrompts({});
      setSystemPrompts([]);
      setSettings({});
      setFootnotes([]);
      setLoading(false);
      return;
    }

    // Load data when user changes or logs in
    checkEncryptionSupport();
    loadInitialData();
  }, [user?.id, isAuthenticated]); // Re-run when user changes

  const checkEncryptionSupport = () => {
    setEncryptionSupported(encryptionService.isSupported());
  };

  const loadInitialData = async () => {
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      if (!encryptionService.isSupported()) {
        console.warn('Encryption not supported in this browser');
        setLoading(false);
        return;
      }
      
      // Load projects
      const projectsResponse = await projectsApi.getAll();
      const projects = projectsResponse.data;
      
      // Decrypt projects if they are encrypted
      const decryptedProjects = await Promise.all(
        projects.map(async (project) => {
          if (project.name_encrypted || project.description_encrypted) {
            return {
              ...project,
              name: project.name_encrypted ? await encryptionService.decrypt(project.name) : project.name,
              description: (project.description && project.description_encrypted) ? 
                await encryptionService.decrypt(project.description) : project.description
            };
          }
          return project;
        })
      );
      
      setProjects(decryptedProjects);
      
      // Load prompts for each project
      const promptsData = {};
      for (const project of decryptedProjects) {
        try {
          const promptsResponse = await promptsApi.getByProject(project.id);
          const prompts = promptsResponse.data;
          
          // Decrypt prompts if they are encrypted
          const decryptedPrompts = await Promise.all(
            prompts.map(async (prompt) => {
              if (prompt.title_encrypted || prompt.content_encrypted) {
                return {
                  ...prompt,
                  title: prompt.title_encrypted ? await encryptionService.decrypt(prompt.title) : prompt.title,
                  content: prompt.content_encrypted ? await encryptionService.decrypt(prompt.content) : prompt.content
                };
              }
              return prompt;
            })
          );
          
          promptsData[project.id] = decryptedPrompts;
        } catch (error) {
          console.warn(`Failed to load prompts for project ${project.id}:`, error);
          promptsData[project.id] = [];
        }
      }
      setPrompts(promptsData);
      
      // Load system prompts
      const systemPromptsResponse = await systemPromptsApi.getAll();
      setSystemPrompts(systemPromptsResponse.data);
      
      // Load footnotes from API
      try {
        const footnotesResponse = await footnotesApi.getAll();
        const footnotes = footnotesResponse.data;
        
        // Decrypt footnotes if they are encrypted
        const decryptedFootnotes = await Promise.all(
          footnotes.map(async (footnote) => {
            if (footnote.name_encrypted || footnote.content_encrypted) {
              return {
                ...footnote,
                name: footnote.name_encrypted ? await encryptionService.decrypt(footnote.name) : footnote.name,
                content: footnote.content_encrypted ? await encryptionService.decrypt(footnote.content) : footnote.content
              };
            }
            return footnote;
          })
        );
        
        setFootnotes(decryptedFootnotes);
      } catch (error) {
        console.warn('Failed to load footnotes:', error);
        setFootnotes([]);
      }
      
      // Load settings
      try {
        const settingsResponse = await settingsApi.get();
        setSettings(settingsResponse.data);
      } catch (error) {
        // Settings might not exist yet, use defaults
        setSettings({
          openrouter_api_key: '',
          selected_model: 'anthropic/claude-3-haiku',
          default_system_prompt: null
        });
      }
      
    } catch (error) {
      console.error('Failed to load initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createProject = async (projectData) => {
    try {
      // Encrypt the name and description before sending to backend
      const encryptedName = await encryptionService.encrypt(projectData.name);
      const encryptedDescription = projectData.description ? 
        await encryptionService.encrypt(projectData.description) : null;
      
      const response = await projectsApi.create({
        ...projectData,
        name: encryptedName,
        description: encryptedDescription,
        name_encrypted: true,
        description_encrypted: !!projectData.description
      });
      
      const newProject = response.data;
      // Decrypt for local state
      newProject.name = await encryptionService.decrypt(newProject.name);
      if (newProject.description && newProject.description_encrypted) {
        newProject.description = await encryptionService.decrypt(newProject.description);
      }
      
      setProjects(prev => [newProject, ...prev]);
      setPrompts(prev => ({ ...prev, [newProject.id]: [] }));
      return newProject;
    } catch (error) {
      console.error('Failed to create project:', error);
      throw error;
    }
  };

  const updateProject = async (projectId, updates) => {
    try {
      // Encrypt the name and description before sending to backend
      const encryptedName = await encryptionService.encrypt(updates.name);
      const encryptedDescription = updates.description ? 
        await encryptionService.encrypt(updates.description) : null;
      
      const response = await projectsApi.update(projectId, {
        ...updates,
        name: encryptedName,
        description: encryptedDescription,
        name_encrypted: true,
        description_encrypted: !!updates.description
      });
      
      const updatedProject = response.data;
      // Decrypt for local state
      updatedProject.name = await encryptionService.decrypt(updatedProject.name);
      if (updatedProject.description && updatedProject.description_encrypted) {
        updatedProject.description = await encryptionService.decrypt(updatedProject.description);
      }
      
      setProjects(prev => prev.map(p => p.id === projectId ? updatedProject : p));
      return updatedProject;
    } catch (error) {
      console.error('Failed to update project:', error);
      throw error;
    }
  };

  const deleteProject = async (projectId) => {
    try {
      await projectsApi.delete(projectId);
      setProjects(prev => prev.filter(p => p.id !== projectId));
      setPrompts(prev => {
        const newPrompts = { ...prev };
        delete newPrompts[projectId];
        return newPrompts;
      });
    } catch (error) {
      console.error('Failed to delete project:', error);
      throw error;
    }
  };

  const createPrompt = async (projectId, promptData) => {
    try {
      // Encrypt the title and content before sending to backend
      const encryptedTitle = await encryptionService.encrypt(promptData.title);
      const encryptedContent = await encryptionService.encrypt(promptData.content);
      
      const response = await promptsApi.create(projectId, {
        ...promptData,
        title: encryptedTitle,
        content: encryptedContent,
        title_encrypted: true,
        content_encrypted: true
      });
      
      const newPrompt = response.data;
      // Decrypt for local state
      newPrompt.title = await encryptionService.decrypt(newPrompt.title);
      newPrompt.content = await encryptionService.decrypt(newPrompt.content);
      
      // Update prompts state - add new prompt to beginning since it's the newest
      setPrompts(prev => ({
        ...prev,
        [projectId]: [newPrompt, ...(prev[projectId] || [])]
      }));
      
      // Update project prompt count separately after prompts are updated
      setProjects(prevProjects => prevProjects.map(p => 
        p.id === projectId ? { ...p, prompt_count: p.prompt_count + 1 } : p
      ));
      
      return newPrompt;
    } catch (error) {
      console.error('Failed to create prompt:', error);
      throw error;
    }
  };

  const updatePrompt = async (projectId, promptId, updates) => {
    try {
      // Encrypt the title and content before sending to backend
      const encryptedTitle = await encryptionService.encrypt(updates.title);
      const encryptedContent = await encryptionService.encrypt(updates.content);
      
      const response = await promptsApi.update(promptId, {
        ...updates,
        title: encryptedTitle,
        content: encryptedContent,
        title_encrypted: true,
        content_encrypted: true
      });
      
      const updatedPrompt = response.data;
      // Decrypt for local state
      updatedPrompt.title = await encryptionService.decrypt(updatedPrompt.title);
      updatedPrompt.content = await encryptionService.decrypt(updatedPrompt.content);
      
      // Remove the old prompt and add the updated one to the beginning (newest first)
      setPrompts(prev => {
        const currentPrompts = prev[projectId] || [];
        const otherPrompts = currentPrompts.filter(p => p.id !== promptId);
        return {
          ...prev,
          [projectId]: [updatedPrompt, ...otherPrompts]
        };
      });
      
      return updatedPrompt;
    } catch (error) {
      console.error('Failed to update prompt:', error);
      throw error;
    }
  };

  const deletePrompt = async (projectId, promptId) => {
    try {
      await promptsApi.delete(promptId);
      
      // Update prompts state
      setPrompts(prev => ({
        ...prev,
        [projectId]: prev[projectId].filter(p => p.id !== promptId)
      }));
      
      // Update project prompt count separately
      setProjects(prevProjects => prevProjects.map(p => 
        p.id === projectId ? { ...p, prompt_count: Math.max(0, p.prompt_count - 1) } : p
      ));
      
    } catch (error) {
      console.error('Failed to delete prompt:', error);
      throw error;
    }
  };

  const createSystemPrompt = async (systemPromptData) => {
    try {
      const response = await systemPromptsApi.create(systemPromptData);
      const newSystemPrompt = response.data;
      setSystemPrompts(prev => [...prev, newSystemPrompt]);
      return newSystemPrompt;
    } catch (error) {
      console.error('Failed to create system prompt:', error);
      throw error;
    }
  };

  const updateSystemPrompt = async (systemPromptId, updates) => {
    try {
      const response = await systemPromptsApi.update(systemPromptId, updates);
      const updatedSystemPrompt = response.data;
      setSystemPrompts(prev => prev.map(sp => 
        sp.id === systemPromptId ? updatedSystemPrompt : sp
      ));
      return updatedSystemPrompt;
    } catch (error) {
      console.error('Failed to update system prompt:', error);
      throw error;
    }
  };

  const deleteSystemPrompt = async (systemPromptId) => {
    try {
      await systemPromptsApi.delete(systemPromptId);
      setSystemPrompts(prev => prev.filter(sp => sp.id !== systemPromptId));
    } catch (error) {
      console.error('Failed to delete system prompt:', error);
      throw error;
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      const response = await settingsApi.update(newSettings);
      setSettings(response.data);
      
      // If default_system_prompt changed, refresh system prompts to update badges
      if (newSettings.default_system_prompt !== undefined) {
        await loadSystemPrompts();
      }
      
      return response.data;
    } catch (error) {
      console.error('Failed to update settings:', error);
      throw error;
    }
  };

  const loadSystemPrompts = async () => {
    try {
      const systemPromptsResponse = await systemPromptsApi.getAll();
      setSystemPrompts(systemPromptsResponse.data);
    } catch (error) {
      console.error('Failed to load system prompts:', error);
      throw error;
    }
  };

  const improvePrompt = async (prompt, systemPromptId) => {
    try {
      // Call backend API endpoint instead of OpenRouter directly
      // This ensures usage tracking is properly handled
      const response = await apiClient.post('/improve-prompt', {
        prompt: prompt,
        system_prompt_id: systemPromptId,
        model: settings.selected_model || 'anthropic/claude-3-haiku'
      });

      return response.data.improved_prompt;
      
    } catch (error) {
      console.error('Failed to improve prompt:', error);
      throw error;
    }
  };

  const createFootnote = async (footnoteData) => {
    try {
      // Encrypt the content before sending to backend
      const encryptedName = await encryptionService.encrypt(footnoteData.name);
      const encryptedContent = await encryptionService.encrypt(footnoteData.content);
      
      const response = await footnotesApi.create({
        name: encryptedName,
        content: encryptedContent,
        name_encrypted: true,
        content_encrypted: true
      });
      
      const newFootnote = response.data;
      // Decrypt for local state
      newFootnote.name = await encryptionService.decrypt(newFootnote.name);
      newFootnote.content = await encryptionService.decrypt(newFootnote.content);
      
      setFootnotes(prevFootnotes => [newFootnote, ...prevFootnotes]);
      return newFootnote;
    } catch (error) {
      console.error('Failed to create footnote:', error);
      throw error;
    }
  };

  const updateFootnote = async (footnoteId, updates) => {
    try {
      // Encrypt the content before sending to backend
      const encryptedName = await encryptionService.encrypt(updates.name);
      const encryptedContent = await encryptionService.encrypt(updates.content);
      
      const response = await footnotesApi.update(footnoteId, {
        name: encryptedName,
        content: encryptedContent,
        name_encrypted: true,
        content_encrypted: true
      });
      
      const updatedFootnote = response.data;
      // Decrypt for local state
      updatedFootnote.name = await encryptionService.decrypt(updatedFootnote.name);
      updatedFootnote.content = await encryptionService.decrypt(updatedFootnote.content);
      
      setFootnotes(prevFootnotes => 
        prevFootnotes.map(f => f.id === footnoteId ? updatedFootnote : f)
      );
      return updatedFootnote;
    } catch (error) {
      console.error('Failed to update footnote:', error);
      throw error;
    }
  };

  const deleteFootnote = async (footnoteId) => {
    try {
      await footnotesApi.delete(footnoteId);
      setFootnotes(prevFootnotes => 
        prevFootnotes.filter(f => f.id !== footnoteId)
      );
    } catch (error) {
      console.error('Failed to delete footnote:', error);
      throw error;
    }
  };

  // Clear encryption key on logout/reset
  const clearEncryptionKey = () => {
    encryptionService.clearKey();
    setProjects([]);
    setPrompts({});
    setSystemPrompts([]);
    setSettings({});
    setFootnotes([]);
  };

  const value = {
    projects,
    prompts,
    systemPrompts,
    settings,
    footnotes,
    loading,
    encryptionSupported,
    createProject,
    updateProject,
    deleteProject,
    createPrompt,
    updatePrompt,
    deletePrompt,
    createSystemPrompt,
    updateSystemPrompt,
    deleteSystemPrompt,
    updateSettings,
    loadSystemPrompts,
    improvePrompt,
    createFootnote,
    updateFootnote,
    deleteFootnote,
    clearEncryptionKey
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};